import React from 'react'
import { useNavigate } from 'react-router-dom'
import styles from 'styles/layout/SelectedReportCategoryHeaderSection.module.sass'
import HeaderNavigation from '../header/HeaderNavigation'
import ReusableIcon from 'components/common/ReusableIcon'
import { CategoryMeta } from 'interface/CategoryMeta'

/**
 * Props for the SelectedReportCategoryHeaderSection component.
 *
 * @interface SelectedReportCategoryHeaderSectionProps
 * @property {string} [title] - The title of the selected report category.
 */
interface SelectedReportCategoryHeaderSectionProps {
    title: string;
    description: string;
    meta: CategoryMeta;
}

/**
 * SelectedReportCategoryHeaderSection component - A header section for the selected report category page.
 * It includes navigation and breadcrumb links to enhance user experience.
 *
 * @component
 *
 * @example
 * // Example usage:
 * <SelectedReportCategoryHeaderSection title="Market Analysis" />
 *
 * @param {SelectedReportCategoryHeaderSectionProps} props - The props for the component.
 * @returns {JSX.Element} The rendered SelectedReportCategoryHeaderSection component.
 */
const SelectedReportCategoryHeaderSection: React.FC<SelectedReportCategoryHeaderSectionProps> = ({ title, description, meta }) => {
    const navigate = useNavigate();

    const handleCrumbClick = (crumb: string) => {
        navigate(`/${crumb}`);
    }
    return (
        <section className={styles['selected-report-category-header-section']}>
            <HeaderNavigation theme='dark' />
            <div className={styles['main-wrapper']}>
                <div className={styles['crumbs-wrapper']}>
                    <span className={`${styles['crumb']} ${styles['active']}`} onClick={() => handleCrumbClick('report/categories')}>Reports</span>
                    <ReusableIcon icon={'tabler:chevron-right'} className={styles['crumb-icon'] || 'crumb-icon'} color="#111111" />
                    <span className={styles['crumb']}>{title}</span>
                </div>
                <div className={styles['header-content']}>
                    <div className={styles['category-icon']} style={{ backgroundColor: meta.secondaryColor }}>
                        <ReusableIcon 
                            icon={meta.icon} 
                            className={styles['icon'] || 'icon'} 
                            color={meta.primaryColor} 
                        />
                    </div>
                    <div className={styles['left-side']}>
                        <h2 className={styles['title']}>{title}</h2>
                        <p className={styles['description']}>{description}</p>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default SelectedReportCategoryHeaderSection